import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { Message } from 'src/app/core/class/message';

import { CommonService } from '../../../../../../core/services/common.service';
import { DocumentMohaService } from '../../../../../../core/services/document-moha.service';

interface StandardizedContractSearchForm {
  nameOrCode: string;
  customer: string;
  statusStandardized: string | null;
  statusDigitalCert: string | null;
}

interface StandardizedContractItem {
  id: number | string;
  stt: number;
  codeName: string;
  parties: string;
  emails: string;
  statusDigitalCert: string;
  statusDigitalCertPersonal: string;
  statusVneidPersonal: string;
  statusBbuqPersonal: string;
}

interface StandardizedContractPaginate {
  currentPage: number;
  pageSize: number;
  totalRow: number;
  totalPage: number;
  totalPageArray: number[];
  no: number[];
}

@Component({
  selector: 'app-labor-contract-standardize-employee',
  templateUrl: './labor-contract-standardize-employee.component.html',
  styleUrls: ['./labor-contract-standardize-employee.component.scss']
})
export class LaborContractStandardizeEmployeeComponent implements OnInit {
  documents: StandardizedContractSearchForm = {
    nameOrCode: '',
    customer: '',
    statusStandardized: null,
    statusDigitalCert: null
  };

  showAdvancedSearch = false;
  isLoading = false;
  checkFocus = true;

  statusStandardizedOptions = [
    { value: 'LACKING_CONDITIONS', label: this.message.getMessage('SEARCH_STANDARDIZED_LABOR_EMPLOYEE.ADDITIONAL_CONDITIONS_REQUIRED') },
    { value: 'DONE', label: this.message.getMessage('SEARCH_STANDARDIZED_LABOR_EMPLOYEE.STANDARDIZED') }
  ];

  digitalCertStatusOptions = [
    { value: 'VALID', label: this.message.getMessage('SEARCH_STANDARDIZED_LABOR_EMPLOYEE.VALID') },
    { value: 'INVALID', label: this.message.getMessage('SEARCH_STANDARDIZED_LABOR_EMPLOYEE.INVALID') }
  ];

  displayedColumns: string[] = [
    'stt',
    'codeName',
    'parties',
    'emails',
    // 'statusDigitalCert',
    'statusDigitalCertPersonal',
    'statusVneidPersonal',
    'statusBbuqPersonal',
    'action'
  ];

  dataSource: { data: StandardizedContractItem[] } = { data: [] };
  paginate: StandardizedContractPaginate = {
    currentPage: 0,
    pageSize: 10,
    totalRow: 0,
    totalPage: 0,
    totalPageArray: [],
    no: []
  };

  constructor(
    public commonService: CommonService,
    private documentMohaService: DocumentMohaService,
    private toastr: ToastrService,
    private router: Router,
    private message: Message
  ) {}

  ngOnInit(): void {
    this.onSearch();
  }

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  onSearch(): void {
    this.loadData(0, this.paginate.pageSize);
  }

  onFilterData(page: number = 0, pageSize: number = 10): void {
    this.loadData(page, pageSize);
  }

  private buildFilterRequest(page: number, pageSize: number): any {
    return {
      nameOrCode: this.documents.nameOrCode || '',
      customer: this.documents.customer || '',
      statusStandardized: this.documents.statusStandardized || '',
      statusDigitalCert: this.showAdvancedSearch && this.documents.statusDigitalCert
        ? this.documents.statusDigitalCert
        : '',
      page,
      pageSize
    };
  }

  private loadData(page: number, pageSize: number): void {
    this.isLoading = true;
    this.documentMohaService.searchStandardizedEmployee(this.buildFilterRequest(page, pageSize))
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(
        (res: any) => this.handleSearchSuccess(res),
        () => this.handleSearchError()
      );
  }

  private handleSearchSuccess(res: any): void {
    const responseData = res && res.data ? res.data : {};
    const rows: any[] = Array.isArray(responseData)
      ? responseData
      : (responseData.dataRow || responseData.rows || []);
    const totalRow = typeof responseData.totalRow === 'number'
      ? responseData.totalRow
      : rows.length;
    const currentPage = typeof responseData.currentPage === 'number'
      ? responseData.currentPage
      : 0;
    const pageSize = typeof responseData.pageSize === 'number' && responseData.pageSize > 0
      ? responseData.pageSize
      : this.paginate.pageSize;
    const totalPage = Math.ceil(totalRow / pageSize);

    this.paginate = {
      currentPage,
      pageSize,
      totalRow,
      totalPage,
      totalPageArray: this.counter(totalPage, 1),
      no: this.counter(pageSize, currentPage * pageSize + 1)
    };
    this.dataSource.data = rows.map((item, index) => this.mapItem(item, index));
  }

  private handleSearchError(): void {
    this.dataSource.data = [];
    this.paginate = {
      ...this.paginate,
      totalRow: 0,
      totalPage: 0,
      totalPageArray: [],
      no: []
    };
    this.toastr.error(this.message.getMessage('SEARCH_STANDARDIZED_LABOR_EMPLOYEE.FAILED_TO_LOAD_STANDARDIZED_LABOR_CONTRACT_LIST'));
  }

  private mapItem(item: any, index: number): StandardizedContractItem {
    return {
      id: this.getResponseField(item, 'id'),
      stt: this.paginate.currentPage * this.paginate.pageSize + index + 1,
      codeName: this.getResponseField(item, 'name'),
      parties: this.getResponseField(item, 'allName'),
      emails: this.getResponseField(item, 'allEmail'),
      statusDigitalCert: this.getResponseField(item, 'statusDigitalCert'),
      statusDigitalCertPersonal: this.getResponseField(item, 'statusDigitalCertPersonal'),
      statusVneidPersonal: this.getResponseField(item, 'statusVneidPersonal'),
      statusBbuqPersonal: this.getResponseField(item, 'statusBbuqPersonal')
    };
  }

  private getResponseField(item: any, fieldName: string): any {
    if (!item) {
      return null;
    }
    if (item[fieldName] !== undefined) {
      return item[fieldName];
    }
    const matchingKey = Object.keys(item).find(key => key.trim() === fieldName);
    return matchingKey ? item[matchingKey] : null;
  }

  getStatusLabel(status: string): string {
    if (status === 'VALID') {
      return this.message.getMessage('SEARCH_STANDARDIZED_LABOR_EMPLOYEE.VALID');
    }
    if (status === 'INVALID') {
      return this.message.getMessage('SEARCH_STANDARDIZED_LABOR_EMPLOYEE.INVALID');
    }
    return status || '-';
  }

  getStatusClass(status: string): string {
    if (status === 'VALID') {
      return 'status-valid';
    }
    if (status === 'INVALID') {
      return 'status-invalid';
    }
    return 'status-empty';
  }

  viewDetail(element: StandardizedContractItem): void {
    if (!element || !element.id) {
      return;
    }

    this.router.navigate(['/home-enterprise/view-document-moha/' + element.id], {
      queryParams: { goFrom: 'standardizedContractEmployee' }
    });
  }

  openPDFByBrowser(documentData: StandardizedContractItem): void {
    if (documentData && documentData.id) {
      this.documentMohaService.downloadFileZip(String(documentData.id)).subscribe(res => {
        const blobUrl = URL.createObjectURL(res);
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = documentData.codeName + '.zip';
        downloadLink.click();
      });
    }
  }

  private counter(length: number, from: number): number[] {
    return new Array(length).fill(0).map((item, index) => index + from);
  }

  getStatusTagColor(status: string): string {
    switch (status) {
      case 'DONE':
      case 'VALID':
        return 'green';
      case 'WAIT':
      case 'PROCESSING':
      case 'LACKING_CONDITIONS':
        return 'blue';
      case 'ERROR':
      case 'INVALID':
      case 'NOT_STANDARDIZED':
        return 'red';
      default:
        return '';
    }
  }
}
